import Dialog from '@mui/material/Dialog';
import React, { useCallback, useMemo, useState } from 'react';
import { useHasEnterpriseFeatures } from '../../hooks/subscriptionHooks';
import { Bot } from '../../models/Bots';
import { UserApp } from '../../models/User';
import { useFlag, useUserSafe } from '../../scripts/hooks';
import { IUpdateBotParams } from '../../scripts/hooks/bots';
import { PaywallModal } from '../admin/EnterprisePaywall/EnterprisePaywall';
import { AuthorDetails } from '../controls/AuthorDetails/AuthorDetails';
import { Modal } from '../controls/ui/Modal/Modal';
import { UIButton } from '../controls/ui/UIButton/UIButton';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';
import { UITextArea } from '../controls/ui/UITextArea/UITextArea';
import { Switch } from '../shadcn/lib/components/switch';
import { AppConnectionsSelect } from './AppConnectionsSelect';

interface IBotsModalProps {
  bot: Bot;
  orgWideEnabledApps: UserApp[];
  closeModal: () => void;
  submit: (params: IUpdateBotParams) => Promise<void>;
}

export const BotsModal: React.FC<IBotsModalProps> = ({
  bot,
  orgWideEnabledApps,
  closeModal,
  submit,
}) => {
  const user = useUserSafe();
  const [isAutorespond, setIsAutorespond] = useState(bot.is_autorespond);
  const [customInstructions, setCustomInstructions] = useState(
    bot.custom_instructions
  );

  const [selectedApps, setSelectedApps] = useState(bot.apps);

  const [paywallModalOpen, setPaywallModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [isAutorespondDialogOpen, setIsAutorespondDialogOpen] = useState(false);

  const botsCustomInstructionsEnabled = useFlag(
    'botsCustomInstructionsEnabled'
  );

  const botAppConnectionsEnabled = useFlag('botsAppConnections');

  const hasEnterpriseFeatures = useHasEnterpriseFeatures();

  const hasEditPermissions = useMemo(
    () => user.admin || bot.user_id === user.userId,
    [bot.user_id, user.admin, user.userId]
  );

  const handleAutorespondChange = useCallback(() => {
    if (isAutorespond) {
      setIsAutorespondDialogOpen(true);
      return;
    }

    setIsAutorespond(!isAutorespond);
  }, [isAutorespond]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    await submit({
      id: bot.id,
      custom_instructions: customInstructions,
      is_autorespond: isAutorespond,
      bot_app_connections: selectedApps.map((app) => app.id),
      enabled_apps: orgWideEnabledApps,
    });

    setLoading(false);

    closeModal();
  }, [
    bot,
    closeModal,
    customInstructions,
    isAutorespond,
    orgWideEnabledApps,
    selectedApps,
    submit,
  ]);

  return (
    <Modal onClose={closeModal}>
      <div className="flex flex-col gap-6">
        <div className="flex gap-2 items-center">
          <UIIcon name="bot" size={32} />
          <h2 className="text-2xl m-0 font-bold">Edit Bot</h2>
        </div>
        <div>
          <div className="font-bold">Slack Channel</div>
          <div className="font-normal mt-2">{bot.bot_name}</div>
        </div>
        <div className="flex gap-10">
          <div>
            <div className="font-bold">Added by</div>
            <div className="mt-2">
              <AuthorDetails
                displayName={bot.author.display_name ?? bot.author.email}
                icon={bot.author.icon}
              />
            </div>
          </div>

          <div>
            <div className="font-bold">Autorespond</div>
            <Switch
              checked={isAutorespond}
              className="mt-2"
              disabled={!hasEditPermissions}
              onCheckedChange={handleAutorespondChange}
            />
          </div>
        </div>
        {botAppConnectionsEnabled && (
          <div>
            <div className="font-bold">App Connections</div>
            {hasEditPermissions ? (
              <AppConnectionsSelect
                apps={orgWideEnabledApps}
                selectedApps={selectedApps}
                setSelectedApps={setSelectedApps}
              />
            ) : (
              <div className="mt-2 flex gap-2">
                {selectedApps.map((app) => (
                  <div className="flex items-center gap-2" key={app.id}>
                    <UIIcon name={app.appName} size={20} type="apps" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {botsCustomInstructionsEnabled && (
          <div>
            {hasEditPermissions ? (
              hasEnterpriseFeatures ? (
                <div className="font-bold mb-2">Instructions (Optional)</div>
              ) : (
                <div className="flex justify-between items-center mb-2">
                  <div className="items-center flex gap-2">
                    <div className="flex p-1 bg-sunrise-10 rounded-full">
                      <UIIcon
                        className="text-white"
                        name="star"
                        size={12}
                        type="ui"
                      />
                    </div>
                    <div className="font-bold">Instructions</div>
                  </div>
                  <UIButton
                    onClick={() => {
                      setPaywallModalOpen(true);
                    }}
                    type="secondary"
                  >
                    <div className="flex gap-1 items-center">
                      <UIIcon name="lock" />
                      Upgrade Plan
                    </div>
                  </UIButton>
                </div>
              )
            ) : (
              <div className="font-bold mb-2">Instructions</div>
            )}
            <UITextArea
              disabled={!hasEditPermissions || !hasEnterpriseFeatures}
              minRows={4}
              multilineExpandable
              onChange={setCustomInstructions}
              placeholder={`For questions about support tickets, prioritize answers from Intercom help center and tickets.

Respond with short answers with no more than 200 words.

If you don't know the answer, ask the user to create a ticket in Linear.`}
              value={
                hasEditPermissions && hasEnterpriseFeatures
                  ? customInstructions
                  : ''
              }
            />
            <div className="mt-1 text-smoke-10 text-xs">
              Tip: Learn best practices for adding Instructions{' '}
              <a
                href="https://support.dashworks.ai/slackbot/slackbot-in-channels/instructions"
                rel="noreferrer"
                target="_blank"
              >
                here
              </a>
              .
            </div>
          </div>
        )}
      </div>
      <footer className="flex justify-between mt-6">
        <UIButton onClick={closeModal} type="secondary">
          Cancel
        </UIButton>
        <UIButton onClick={handleSubmit} processing={loading}>
          Save
        </UIButton>
      </footer>

      <AutoRespondDialog
        closeModal={() => {
          setIsAutorespondDialogOpen(false);
        }}
        open={isAutorespondDialogOpen}
        submit={() => {
          setIsAutorespond(!isAutorespond);
          setIsAutorespondDialogOpen(false);
        }}
      />

      {paywallModalOpen && (
        <PaywallModal
          closeModal={() => {
            setPaywallModalOpen(false);
          }}
        />
      )}
    </Modal>
  );
};

const AutoRespondDialog = ({
  open,
  closeModal,
  submit,
}: {
  open: boolean;
  closeModal: () => void;
  submit: () => void;
}) => {
  return (
    <Dialog open={open}>
      <div className="flex p-6 flex-col">
        <div className="text-lg font-semibold">Are you sure?</div>
        <div className="text-cloud-40 mt-2">
          Dash AI will not automatically respond to any questions in this
          channel unless it is tagged.
        </div>
        <div className="flex justify-between mt-4">
          <UIButton onClick={closeModal} type="secondary">
            Cancel
          </UIButton>
          <UIButton
            onClick={() => {
              submit();
            }}
            type="primary"
          >
            Confirm
          </UIButton>
        </div>
      </div>
    </Dialog>
  );
};
